import React, { useState } from "react";
import { Download, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useTranslation } from "../../../i18n";
import analyticsService from "../../../services/analyticsService";

const DATASETS = ["recognitions", "participation", "teams", "transactions", "approvals", "redemptions", "nominations"];

const ExportTab = ({ filters, scope }) => {
  const { t } = useTranslation("analytics");
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState({});

  const download = async (dataset) => {
    setBusy(dataset);
    setStatus((s) => ({ ...s, [dataset]: null }));
    try {
      const filename = await analyticsService.exportCsv(dataset, filters);
      setStatus((s) => ({ ...s, [dataset]: { ok: true, filename } }));
    } catch (err) {
      setStatus((s) => ({ ...s, [dataset]: { ok: false, message: err?.message || t("load_failed") } }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-purple-600" aria-hidden="true" />
          {t("export.title")}
        </h3>
        <p className="text-sm text-slate-500 mt-1">{t("export.subtitle")}</p>
        {scope && (
          <p className="text-xs text-slate-400 mt-1">
            {t("export.scope", { start: scope.start_date, end: scope.end_date })}
            {scope.team_id ? ` · ${t("export.team_filter")}` : ""}
            {scope.recognition_type_id ? ` · ${t("export.type_filter")}` : ""}
          </p>
        )}
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {DATASETS.map((ds) => {
          const st = status[ds];
          return (
            <li key={ds} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800">{t(`export.datasets.${ds}.title`)}</p>
                <p className="text-xs text-slate-500 mt-0.5">{t(`export.datasets.${ds}.description`)}</p>
                {st?.ok && (
                  <p className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" aria-hidden="true" /> {st.filename}
                  </p>
                )}
                {st && !st.ok && (
                  <p className="text-[11px] text-rose-700 mt-1 inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" aria-hidden="true" /> {st.message}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => download(ds)}
                disabled={busy !== null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 disabled:opacity-50 flex-shrink-0"
              >
                {busy === ds ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <Download className="w-3.5 h-3.5" aria-hidden="true" />}
                {t("export.download")}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ExportTab;
